// Centralized API/auth helpers.
// Strategy:
//  - Default cookies stay enabled (withCredentials=true) for same-site setups
//    (preview, future api.<custom-domain> CNAME).
//  - On cross-site setups (custom domain ↔ emergent.host), browsers block 3rd-party
//    cookies. We therefore ALSO send Authorization: Bearer <access_token> on every
//    request, applied via axios.defaults.headers.common (so all existing axios.*
//    calls in the codebase pick it up — no per-page refactor needed).
//  - On 401, we attempt one transparent token refresh using the stored refresh_token
//    via Authorization header, then replay the original request once.

import axios from "axios";

const STORAGE_KEY_ACCESS = "cbm_access_token";
const STORAGE_KEY_REFRESH = "cbm_refresh_token";

// Cookies stay enabled globally — costless and useful when same-site.
axios.defaults.withCredentials = true;

export function getStoredAccessToken() {
  try { return localStorage.getItem(STORAGE_KEY_ACCESS); } catch { return null; }
}
export function getStoredRefreshToken() {
  try { return localStorage.getItem(STORAGE_KEY_REFRESH); } catch { return null; }
}

export function setAuthTokens({ access_token, refresh_token } = {}) {
  try {
    if (access_token) {
      localStorage.setItem(STORAGE_KEY_ACCESS, access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    }
    if (refresh_token) {
      localStorage.setItem(STORAGE_KEY_REFRESH, refresh_token);
    }
  } catch {
    // localStorage unavailable — keep header in memory only
    if (access_token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    }
  }
}

export function clearAuthTokens() {
  try {
    localStorage.removeItem(STORAGE_KEY_ACCESS);
    localStorage.removeItem(STORAGE_KEY_REFRESH);
    // Legacy keys cleanup
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("access_token");
  } catch {}
  delete axios.defaults.headers.common["Authorization"];
}

// Boot-time: re-apply token to axios so the very first request after a refresh
// already carries Authorization. Called from index.js / App.js.
export function bootstrapAuthHeaders() {
  const t = getStoredAccessToken();
  if (t) axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
}

// ── Transparent refresh on 401 ──────────────────────────────────────────────
const API = process.env.REACT_APP_BACKEND_URL;
let refreshing = null; // single-flight

async function doRefresh() {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) throw new Error("no_refresh_token");
  const { data } = await axios.post(
    `${API}/api/auth/refresh`,
    {},
    {
      headers: { Authorization: `Bearer ${refreshToken}` },
      // Avoid the interceptor from triggering itself
      _skipAuthRefresh: true,
    }
  );
  if (!data?.access_token) throw new Error("no_access_token_returned");
  setAuthTokens({ access_token: data.access_token });
  return data.access_token;
}

axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (
      status === 401 &&
      original &&
      !original._retry &&
      !original._skipAuthRefresh &&
      getStoredRefreshToken()
    ) {
      original._retry = true;
      try {
        if (!refreshing) refreshing = doRefresh().finally(() => { refreshing = null; });
        const newToken = await refreshing;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return axios(original);
      } catch (e) {
        clearAuthTokens();
        // Hard redirect to login (avoid loops on protected pages)
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.replace("/login");
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);
